import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Share2, Printer } from 'lucide-react';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ThemeToggle';
import NoteViewer from '../components/NoteViewer';
import ShareDialog from '../components/ui/ShareDialog';
import { useAuthStore } from '../store/auth-store';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import type { Note } from '../types';

const NotePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [note, setNote] = useState<Note | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);

  useEffect(() => {
    const fetchNote = async () => {
      try {
        const { data, error } = await supabase
          .from('notes')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;

        if (!data) {
          navigate('/404');
          return;
        }

        // Check if user has access
        if (!data.is_public && (!user || user.id !== data.user_id)) {
          navigate('/404');
          return;
        }

        // Increment view count for public notes
        if (data.is_public && (!user || user.id !== data.user_id)) {
          await supabase.rpc('increment_note_views', { note_id_param: data.id });
        }

        setNote({
          ...data,
          createdAt: new Date(data.created_at),
          updatedAt: new Date(data.updated_at)
        });
      } catch (error) {
        console.error('Error fetching note:', error);
        navigate('/404');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNote();
  }, [id, user, navigate]);

  const handleTogglePublic = async () => {
    if (!note || !user) return;

    try {
      const { error } = await supabase
        .from('notes')
        .update({ is_public: !note.is_public })
        .eq('id', note.id)
        .eq('user_id', user.id);

      if (error) throw error;

      setNote(prev => prev ? {
        ...prev,
        is_public: !prev.is_public
      } : null);

      toast.success(
        note.is_public 
          ? 'Note is now private' 
          : 'Note is now public'
      );
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5 flex items-center justify-center">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-muted rounded" />
          <div className="h-4 w-48 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!note) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <header className="sticky top-0 border-b bg-background/80 backdrop-blur-sm z-40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            {user?.id === note.user_id && (
              <Button
                variant="ghost"
                onClick={() => setShowShareDialog(true)}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <NoteViewer
            note={note}
            onClose={() => navigate(-1)}
            onShare={() => setShowShareDialog(true)}
            user={user ? {
              name: user.name,
              avatar_url: user.avatar_url
            } : undefined}
          />
        </motion.div>
      </main>

      <ShareDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        isPublic={note.is_public}
        onTogglePublic={handleTogglePublic}
        noteId={note.id}
      />
    </div>
  );
};

export default NotePage;
